import { Avatar, Box, Divider, Paper, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";

import logo from "../../assets/logo.png";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { IExhibition } from "../../interfaces/IExhibition";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";

// Estilos para impressão
const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 15mm;
    }
    body {
      font-size: 12px !important;
      line-height: 1.3 !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  }
`;

// Adiciona os estilos de impressão ao head
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = printStyles;
  document.head.appendChild(styleSheet);
}

interface IBestCollector {
  name: string;
  total: number;
}

export const BestCollectors: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;
  const [bestCollectors, setBestCollectors] = useState<IBestCollector[]>([]);
  const [loading, setLoading] = useState(false);
  const [exhibition, setExhibition] = useState<IExhibition | null>(null);

  const handleListBestCollectors = useCallback(async () => {
    const id = window.location.pathname.replace("/best-collectors/", "");
    if (!id) return;

    setLoading(true);
    try {
      const response = await api.get(`/exhibitions/best-collectors/${id}`);
      setBestCollectors(response.data);
    } catch (error: any) {
      setBestCollectors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleListExhibition = useCallback(async () => {
    const id = window.location.pathname.replace("/best-collectors/", "");
    if (!id) return;

    try {
      const response = await api.get(`/exhibitions/${id}`);
      setExhibition(response.data);
    } catch (error: any) {
      console.error("Erro ao buscar dados da exposição:", error);
      setExhibition(null);
    }
  }, []);

  useEffect(() => {
    handleListBestCollectors();
    handleListExhibition();
  }, [handleListBestCollectors, handleListExhibition]);

  return (
    <>
      {loading && <BackdropCustom />}
      <Box
        sx={{
          p: { xs: 0.5, md: 1 },
          width: "100%",
          maxWidth: "210mm", // Largura A4
          mx: "auto",
          "@media print": {
            p: 1,
            maxWidth: "100%",
            height: "297mm", // Altura A4
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            mb: 1,
            gap: 0.5,
            "@media print": {
              mb: 0.5,
              gap: 0.3,
            },
          }}
        >
          <Avatar
            src={logo}
            alt="Logo"
            sx={{
              width: 50,
              height: 50,
              mb: 0.5,
              bgcolor: "transparent",
              "@media print": {
                width: 40,
                height: 40,
                mb: 0.3,
              },
            }}
            variant="square"
          />
          <Typography
            variant="h5"
            fontWeight={700}
            align="center"
            color="primary"
            sx={{
              "@media print": {
                fontSize: "1.2rem",
              },
            }}
          >
            Relatório dos Melhores Colecionadores
          </Typography>

          {exhibition ? (
            <Paper
              sx={{
                p: 1.5,
                mt: 0.5,
                mb: 0.5,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                textAlign: "center",
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                width: "100%",
                maxWidth: "600px",
                "@media print": {
                  boxShadow: "none",
                  p: 1,
                  mt: 0.3,
                  mb: 0.3,
                },
              }}
              elevation={0}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  mb: 1,
                  "@media print": {
                    fontSize: "1rem",
                    mb: 0.5,
                  },
                }}
              >
                {exhibition.name}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.5,
                  "@media print": {
                    gap: 0.3,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    "@media print": {
                      opacity: 1,
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  📅{" "}
                  {exhibition.date
                    ? dayjs(exhibition.date).format("DD/MM/YYYY")
                    : "Não informada"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    "@media print": {
                      opacity: 1,
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  📍 {exhibition.city}
                  {exhibition.state?.name && ` - ${exhibition.state.name}`}
                </Typography>

                {exhibition.president && (
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      "@media print": {
                        opacity: 1,
                        fontSize: "0.7rem",
                      },
                    }}
                  >
                    👨‍💼 {exhibition.president}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  mt: 1,
                  pt: 0.5,
                  borderTop: "1px solid rgba(255,255,255,0.3)",
                  "@media print": {
                    borderTop: "1px solid #ccc",
                    mt: 0.5,
                    pt: 0.3,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    "@media print": {
                      opacity: 1,
                      fontSize: "0.65rem",
                    },
                  }}
                >
                  Status:{" "}
                  {exhibition.finished ? "🏁 Finalizada" : "🚀 Em andamento"}
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 1,
                mt: 0.5,
                mb: 0.5,
                background: "#f5f5f5",
                borderRadius: 1,
                textAlign: "center",
                "@media print": {
                  display: "none",
                },
              }}
              elevation={0}
            >
              <Typography variant="body2" color="text.secondary">
                Carregando informações da exposição...
              </Typography>
            </Paper>
          )}
        </Box>

        <Paper
          sx={{
            p: { xs: 1, md: 1.5 },
            mb: 1,
            background: "#fafbfc",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: 2,
            "@media print": {
              boxShadow: "none",
              p: 0.5,
              mb: 0,
            },
          }}
          elevation={0}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            gutterBottom
            color="primary"
            sx={{
              mb: 1,
              "@media print": {
                fontSize: "1rem",
                mb: 0.5,
              },
            }}
          >
            Ranking dos Colecionadores
          </Typography>
          <Divider
            sx={{
              mb: 1,
              "@media print": {
                mb: 0.5,
              },
            }}
          />

          {bestCollectors.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}
            >
              Nenhum coletor encontrado para esta exposição.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                "@media print": {
                  gap: 0.3,
                },
              }}
            >
              {bestCollectors
                .sort((a, b) => b.total - a.total)
                .map((collector, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1,
                      backgroundColor:
                        index === 0
                          ? "#e8f5e8"
                          : index === 1
                            ? "#fff3e0"
                            : index === 2
                              ? "#fce4ec"
                              : "white",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        index === 0
                          ? "#4caf50"
                          : index === 1
                            ? "#ff9800"
                            : index === 2
                              ? "#e91e63"
                              : "#e0e0e0",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      },
                      "@media print": {
                        p: 0.5,
                        "&:hover": {
                          transform: "none",
                          boxShadow: "none",
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          backgroundColor:
                            index === 0
                              ? "#4caf50"
                              : index === 1
                                ? "#ff9800"
                                : index === 2
                                  ? "#e91e63"
                                  : "#757575",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                          "@media print": {
                            width: 25,
                            height: 25,
                            fontSize: "0.75rem",
                          },
                        }}
                      >
                        {index + 1}º
                      </Box>
                      <Typography
                        variant="body1"
                        fontWeight={index < 3 ? 600 : 500}
                        sx={{
                          color: index < 3 ? "#333" : "#666",
                          "@media print": {
                            fontSize: "0.85rem",
                          },
                        }}
                      >
                        {collector.name}
                      </Typography>
                      {index === 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: "#4caf50",
                            color: "white",
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontWeight: "bold",
                            fontSize: "0.7rem",
                            "@media print": {
                              fontSize: "0.6rem",
                            },
                          }}
                        >
                          🏆 CAMPEÃO
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        color:
                          index === 0
                            ? "#4caf50"
                            : index === 1
                              ? "#ff9800"
                              : index === 2
                                ? "#e91e63"
                                : "#333",
                        "@media print": {
                          fontSize: "1rem",
                        },
                      }}
                    >
                      {collector.total.toFixed(1)} pts
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}

          {bestCollectors.length > 0 && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid #e0e0e0",
                "@media print": {
                  mt: 0.5,
                  pt: 0.5,
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                sx={{
                  "@media print": {
                    fontSize: "0.7rem",
                    color: "#666",
                  },
                }}
              >
                Total: {bestCollectors.length} Colecionadores
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};
